import { useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useOptimizerStore } from "../stores/optimizerStore";
import { useBacktestStore } from "../stores/backtestStore";
import CandidateCard from "../components/optimizer/CandidateCard";
import OptimizerJobCard from "../components/optimizer/OptimizerJobCard";

export default function OptimizerResultsPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const {
    activeJob,
    isLoadingActiveJob,
    fetchJob,
    startPolling,
    stopPolling,
  } = useOptimizerStore();

  useEffect(() => {
    if (jobId) {
      fetchJob(jobId);
    }
    return () => {
      if (jobId) stopPolling(jobId);
    };
  }, [jobId, fetchJob, stopPolling]);

  // Start polling if job is running
  useEffect(() => {
    if (activeJob?.status === "running" && jobId) {
      startPolling(jobId);
    }
  }, [activeJob?.status, jobId, startPolling]);

  // Elapsed time — prefer execution_ms, fall back to date math
  const elapsed = useMemo(() => {
    if (!activeJob) return "";
    if (activeJob.execution_ms) {
      const seconds = Math.floor(activeJob.execution_ms / 1000);
      if (seconds < 60) return `${seconds}s`;
      return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    }
    const start = new Date(activeJob.created_at).getTime();
    const end = activeJob.completed_at
      ? new Date(activeJob.completed_at).getTime()
      : Date.now();
    if (isNaN(start) || isNaN(end)) return "";
    const seconds = Math.floor((end - start) / 1000);
    if (seconds < 60) return `${seconds}s`;
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  }, [activeJob]);

  if (isLoadingActiveJob || !activeJob) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-400"></div>
      </div>
    );
  }

  // Extract strategy name — prefer API field, fall back to DSL parsing
  const strategyName = activeJob.strategy_name
    || activeJob.source_dsl?.match(/^strategy\s+"([^"]+)"/)?.[1]
    || "Unnamed Strategy";

  const sourceMetrics = activeJob.source_metrics;
  const requestPayload = activeJob.request_payload;
  const candidates = activeJob.candidates || [];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back Button */}
      <button
        onClick={() => navigate("/optimizer/jobs")}
        className="flex items-center gap-2 text-surface-400 hover:text-surface-200 mb-6 text-sm"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
        Back to Jobs
      </button>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-surface-100 mb-1">
          {strategyName}
        </h1>
        <p className="text-surface-400">
          {activeJob.status === "completed"
            ? `Completed ${new Date(activeJob.completed_at || activeJob.created_at).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })} · ${activeJob.generations_run || 0} generations · ${activeJob.evaluations_run || 0} evaluations · ${elapsed}`
            : activeJob.status === "running"
              ? "Optimization in progress..."
              : activeJob.status === "failed"
                ? `Failed: ${activeJob.error_message || "Unknown error"}`
                : "Pending..."}
        </p>
      </div>

      {/* Running: Show Progress */}
      {activeJob.status === "running" && (
        <div className="mb-8">
          <OptimizerJobCard job={activeJob} />
        </div>
      )}

      {/* Original Strategy Metrics */}
      {sourceMetrics && activeJob.status === "completed" && (
        <div className="mb-8 p-4 rounded-xl bg-surface-800/50 border border-surface-700/50">
          <h3 className="text-sm font-medium text-surface-400 mb-3">
            Original Strategy
          </h3>
          <div className="grid grid-cols-4 gap-4">
            {sourceMetrics.cagr !== undefined && (
              <div className="text-center">
                <div className="text-lg font-bold text-surface-200">
                  {sourceMetrics.cagr.toFixed(1)}%
                </div>
                <div className="text-xs text-surface-500">CAGR</div>
              </div>
            )}
            {sourceMetrics.sharpe !== undefined && (
              <div className="text-center">
                <div className="text-lg font-bold text-surface-200">
                  {sourceMetrics.sharpe.toFixed(2)}
                </div>
                <div className="text-xs text-surface-500">Sharpe</div>
              </div>
            )}
            {sourceMetrics.max_drawdown !== undefined && (
              <div className="text-center">
                <div className="text-lg font-bold text-surface-200">
                  {sourceMetrics.max_drawdown.toFixed(1)}%
                </div>
                <div className="text-xs text-surface-500">Max DD</div>
              </div>
            )}
            {sourceMetrics.sortino !== undefined && (
              <div className="text-center">
                <div className="text-lg font-bold text-surface-200">
                  {sourceMetrics.sortino.toFixed(2)}
                </div>
                <div className="text-xs text-surface-500">Sortino</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Backtest Original Button */}
      {activeJob.status === "completed" && activeJob.source_dsl && (
        <div className="mb-8 flex gap-3">
          <button
            onClick={() => {
              useBacktestStore.getState().setStrategyCode(activeJob.source_dsl!);
              navigate("/backtest");
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-surface-800/50 text-surface-300 border border-surface-700/50 hover:bg-surface-700/50 hover:text-surface-100 transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Backtest Original Strategy
          </button>
        </div>
      )}

      {/* Candidates */}
      {activeJob.status === "completed" && candidates.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-surface-100 mb-4">
            {candidates.length} Candidate{candidates.length !== 1 ? "s" : ""}{" "}
            Found
          </h2>
          <div className="space-y-4">
            {candidates.map((candidate, index) => (
              <CandidateCard
                key={candidate.strategy_hash || index}
                candidate={candidate}
                rank={index + 1}
                sourceMetrics={sourceMetrics}
              />
            ))}
          </div>
        </div>
      )}

      {/* No Candidates */}
      {activeJob.status === "completed" && candidates.length === 0 && (
        <div className="mb-8 p-6 rounded-xl bg-surface-800/30 border border-surface-700/30 text-center">
          <div className="text-4xl mb-3">🧬</div>
          <h3 className="text-lg font-semibold text-surface-200 mb-2">
            No Improved Candidates Found
          </h3>
          <p className="text-surface-400 text-sm max-w-md mx-auto">
            The optimizer ran {activeJob.generations_run || 0} generations with {activeJob.evaluations_run || 0} evaluations
            but couldn't find strategies that beat the original while preserving constraints.
            Try adjusting targets, relaxing preserve thresholds, or increasing generations.
          </p>
        </div>
      )}

      {/* Configuration Used */}
      {requestPayload && activeJob.status === "completed" && (
        <div className="p-4 rounded-xl bg-surface-800/30 border border-surface-700/30">
          <h3 className="text-sm font-medium text-surface-400 mb-3">
            Configuration Used
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
            <div className="text-surface-300">
              <span className="text-surface-500">Population: </span>
              {requestPayload.population_size || 50}
            </div>
            <div className="text-surface-300">
              <span className="text-surface-500">Generations: </span>
              {requestPayload.max_generations || 20}
            </div>
            <div className="text-surface-300">
              <span className="text-surface-500">Evaluations: </span>
              {requestPayload.max_evaluations || 400}
            </div>
            <div className="text-surface-300">
              <span className="text-surface-500">Crossover: </span>
              {requestPayload.crossover_prob || 0.7}
            </div>
            <div className="text-surface-300">
              <span className="text-surface-500">Mutation: </span>
              {requestPayload.mutation_prob || 0.3}
            </div>
            <div className="text-surface-300">
              <span className="text-surface-500">Stagnation: </span>
              {requestPayload.stagnation_limit || 5}
            </div>
            <div className="text-surface-300">
              <span className="text-surface-500">Tree Depth: </span>
              {requestPayload.max_tree_depth || 15}
            </div>
            <div className="text-surface-300">
              <span className="text-surface-500">Capital: </span>$
              {(requestPayload.initial_capital || 100000).toLocaleString()}
            </div>
            <div className="text-surface-300">
              <span className="text-surface-500">Overfit: </span>
              {requestPayload.overfit_threshold || 0.5}
            </div>
            {requestPayload.target_metric && (
              <div className="text-surface-300">
                <span className="text-surface-500">Target: </span>
                {requestPayload.target_metric.toUpperCase()} →{" "}
                {requestPayload.target_value}
              </div>
            )}
            {requestPayload.preserve &&
              Object.keys(requestPayload.preserve).length > 0 && (
                <div className="text-surface-300 col-span-2">
                  <span className="text-surface-500">Preserve: </span>
                  {Object.entries(requestPayload.preserve)
                    .map(([k, v]) => `${k.toUpperCase()} ≥ ${v}`)
                    .join(", ")}
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
}
